/**
 * DECKSTR.GG — ArenaTracker Component
 * ────────────────────────────────────
 * Live MTGA game state tracker that runs entirely in the browser.
 * Uses the File System Access API to read Player.log and publishes
 * game state to the OBS overlay via Supabase Realtime.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { ArenaPipeline, type PipelineState, type PipelineCallbacks } from '../lib/arena/pipeline';
import { ArenaFileWatcher } from '../lib/arena/file-watcher';
import type { CardFocusPayload } from '../lib/arena/browser-publisher';

// ─── Component ────────────────────────────────────

interface ArenaTrackerProps {
    twitchUsername: string;
}

export function ArenaTracker({ twitchUsername }: ArenaTrackerProps) {
    const pipelineRef = useRef<ArenaPipeline | null>(null);
    const [state, setState] = useState<PipelineState>({
        status: 'idle',
        matchActive: false,
        matchId: null,
        turnNumber: null,
        isMyTurn: false,
        myLife: null,
        oppLife: null,
        zones: { hand: 0, library: 0, graveyard: 0, exile: 0, battlefield: 0, lands: 0 },
        oppZones: { handSize: 0, creatures: 0, lands: 0, openMana: 0 },
        lastCardFocus: null,
        snapshotCount: 0,
        fileName: '',
        channelName: twitchUsername,
        error: null,
    });
    const [logs, setLogs] = useState<string[]>([]);
    const [showLogs, setShowLogs] = useState(false);
    const [lastCard, setLastCard] = useState<CardFocusPayload | null>(null);

    const isSupported = ArenaFileWatcher.isSupported();

    const addLog = useCallback((msg: string) => {
        const ts = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        setLogs(prev => [`[${ts}] ${msg}`, ...prev].slice(0, 100));
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            pipelineRef.current?.disconnect();
        };
    }, []);

    const handleConnect = useCallback(async () => {
        if (!twitchUsername) {
            addLog('Error: Set your Twitch username in Step 2 first.');
            return;
        }

        const callbacks: PipelineCallbacks = {
            onState: setState,
            onLog: addLog,
            onCardFocus: setLastCard,
        };

        const pipeline = new ArenaPipeline(twitchUsername, callbacks);
        pipelineRef.current = pipeline;

        addLog('Opening file picker...');
        const selected = await pipeline.selectFile();
        if (!selected) {
            addLog('File selection cancelled.');
            return;
        }

        addLog('Starting pipeline...');
        await pipeline.start();
    }, [twitchUsername, addLog]);

    const handleDisconnect = useCallback(() => {
        pipelineRef.current?.disconnect();
        pipelineRef.current = null;
        setState(prev => ({
            ...prev,
            status: 'idle',
            matchActive: false,
            snapshotCount: 0,
            fileName: '',
            error: null,
        }));
        setLastCard(null);
        addLog('Disconnected.');
    }, [addLog]);

    const isActive = state.status === 'running' || state.status === 'connected';
    const statusColor = {
        idle: '#6b7280',
        selecting: '#f59e0b',
        connected: '#22c55e',
        running: '#22c55e',
        error: '#ef4444',
    }[state.status];

    const statusLabel = {
        idle: 'Disconnected',
        selecting: 'Selecting file...',
        connected: 'Connected (waiting for game)',
        running: state.matchActive ? `In Match — Turn ${state.turnNumber ?? '?'}` : 'Connected (between games)',
        error: state.error || 'Error',
    }[state.status];

    return (
        <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '16px',
        }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <span style={{ fontSize: '20px' }}>🎮</span>
                <div>
                    <h3 style={{
                        margin: 0,
                        fontSize: '16px',
                        fontWeight: 700,
                        color: '#e2e8f0',
                        letterSpacing: '0.5px',
                    }}>
                        MTGA Live Tracker
                    </h3>
                    <p style={{
                        margin: 0,
                        fontSize: '12px',
                        color: '#94a3b8',
                        marginTop: '2px',
                    }}>
                        Connect your game — your OBS overlay shows live card previews and game state
                    </p>
                </div>
            </div>

            {/* Browser Support Warning */}
            {!isSupported && (
                <div style={{
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#fca5a5',
                }}>
                    ⚠️ Your browser doesn't support the File System Access API.
                    Please use <strong>Chrome</strong> or <strong>Edge</strong> to use this feature.
                </div>
            )}

            {/* Connect / Disconnect */}
            {!isActive ? (
                <div style={{
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px dashed rgba(139, 92, 246, 0.3)',
                }}>
                    <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#94a3b8' }}>
                        Click below and select your MTGA <code style={{
                            background: 'rgba(0,0,0,0.3)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#c4b5fd',
                        }}>Player.log</code> file:
                    </p>
                    <p style={{ margin: '0 0 12px', fontSize: '11px', color: '#64748b' }}>
                        📂 Usually at: <code style={{
                            background: 'rgba(0,0,0,0.3)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            color: '#94a3b8',
                        }}>%APPDATA%\..\LocalLow\Wizards Of The Coast\MTGA\Player.log</code>
                    </p>
                    <button
                        onClick={handleConnect}
                        disabled={!isSupported || !twitchUsername}
                        style={{
                            background: twitchUsername
                                ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)'
                                : 'rgba(100, 116, 139, 0.3)',
                            color: twitchUsername ? '#fff' : '#64748b',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: twitchUsername ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease',
                        }}
                        title={!twitchUsername ? 'Set your Twitch username in Step 2 first' : ''}
                    >
                        🔌 Connect MTGA
                    </button>
                    {!twitchUsername && (
                        <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#f97316' }}>
                            ↑ Set your Twitch username in Step 2 first
                        </p>
                    )}
                </div>
            ) : (
                <>
                    {/* Status Row */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '8px',
                        marginBottom: '12px',
                    }}>
                        {/* Status */}
                        <div style={{
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '8px',
                            padding: '10px 12px',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: statusColor,
                                    boxShadow: `0 0 6px ${statusColor}`,
                                    display: 'inline-block',
                                    animation: isActive ? 'pulse 2s infinite' : 'none',
                                }} />
                                <span style={{ fontSize: '12px', color: '#e2e8f0', fontWeight: 600 }}>
                                    {statusLabel}
                                </span>
                            </div>
                            <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px' }}>
                                📁 {state.fileName || 'No file'}
                            </div>
                        </div>

                        {/* Channel */}
                        <div style={{
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '8px',
                            padding: '10px 12px',
                        }}>
                            <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                                Publishing to
                            </div>
                            <div style={{ fontSize: '12px', color: '#c4b5fd', fontFamily: 'monospace' }}>
                                arena-gamestate-{twitchUsername}
                            </div>
                            <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                                {state.snapshotCount} snapshots
                            </div>
                        </div>
                    </div>

                    {/* Game State (only when match is active) */}
                    {state.matchActive && (
                        <div style={{
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '12px',
                        }}>
                            {/* Life Totals Row */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '10px',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontSize: '14px' }}>❤️</span>
                                    <span style={{ fontSize: '20px', fontWeight: 700, color: '#22c55e' }}>
                                        {state.myLife ?? '?'}
                                    </span>
                                    <span style={{ fontSize: '11px', color: '#64748b' }}>You</span>
                                </div>
                                <div style={{
                                    fontSize: '11px',
                                    color: state.isMyTurn ? '#22c55e' : '#94a3b8',
                                    fontWeight: state.isMyTurn ? 700 : 400,
                                    padding: '2px 8px',
                                    background: state.isMyTurn ? 'rgba(34, 197, 94, 0.15)' : 'rgba(0,0,0,0.2)',
                                    borderRadius: '4px',
                                }}>
                                    T{state.turnNumber ?? '?'} — {state.isMyTurn ? 'YOUR TURN' : 'Opp turn'}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontSize: '11px', color: '#64748b' }}>Opp</span>
                                    <span style={{ fontSize: '20px', fontWeight: 700, color: '#ef4444' }}>
                                        {state.oppLife ?? '?'}
                                    </span>
                                    <span style={{ fontSize: '14px' }}>💀</span>
                                </div>
                            </div>

                            {/* Zones Grid */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: '6px',
                                fontSize: '11px',
                            }}>
                                <ZonePill emoji="🃏" label="Hand" count={state.zones.hand} />
                                <ZonePill emoji="⚔️" label="Board" count={state.zones.battlefield} />
                                <ZonePill emoji="🌍" label="Lands" count={state.zones.lands} />
                                <ZonePill emoji="💀" label="GY" count={state.zones.graveyard} />
                                <ZonePill emoji="✨" label="Exile" count={state.zones.exile} />
                                <ZonePill emoji="🤚" label="Opp Hand" count={state.oppZones.handSize} />
                            </div>
                        </div>
                    )}

                    {/* Last Card Focus */}
                    {lastCard && (
                        <div style={{
                            background: 'rgba(139, 92, 246, 0.1)',
                            border: '1px solid rgba(139, 92, 246, 0.2)',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                        }}>
                            {lastCard.imageUrl && (
                                <img
                                    src={lastCard.imageUrl}
                                    alt={lastCard.name}
                                    style={{
                                        width: '40px',
                                        height: '56px',
                                        borderRadius: '4px',
                                        objectFit: 'cover',
                                    }}
                                />
                            )}
                            <div>
                                <div style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: 600 }}>
                                    {lastCard.name}
                                </div>
                                <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                    {lastCard.transition.toUpperCase()} • {lastCard.type ?? ''}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Controls */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                            onClick={handleDisconnect}
                            style={{
                                background: 'rgba(239, 68, 68, 0.15)',
                                color: '#fca5a5',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                borderRadius: '6px',
                                padding: '6px 14px',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            ⏹ Disconnect
                        </button>
                        <button
                            onClick={() => setShowLogs(v => !v)}
                            style={{
                                background: 'rgba(0,0,0,0.2)',
                                color: '#94a3b8',
                                border: '1px solid rgba(100, 116, 139, 0.2)',
                                borderRadius: '6px',
                                padding: '6px 14px',
                                fontSize: '12px',
                                cursor: 'pointer',
                            }}
                        >
                            {showLogs ? '▼ Hide Logs' : '▶ Show Logs'}
                        </button>
                    </div>

                    {/* Log Output */}
                    {showLogs && (
                        <div style={{
                            marginTop: '10px',
                            background: 'rgba(0, 0, 0, 0.4)',
                            borderRadius: '6px',
                            padding: '8px 10px',
                            maxHeight: '150px',
                            overflowY: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '10px',
                            color: '#94a3b8',
                            lineHeight: '1.6',
                        }}>
                            {logs.length === 0 ? (
                                <span style={{ color: '#475569' }}>No logs yet...</span>
                            ) : (
                                logs.map((log, i) => (
                                    <div key={i} style={{
                                        color: log.includes('Error') || log.includes('failed')
                                            ? '#fca5a5'
                                            : log.includes('[card]')
                                                ? '#c4b5fd'
                                                : '#94a3b8',
                                    }}>
                                        {log}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </>
            )}

            {/* CSS Animation */}
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
        </div>
    );
}

// ─── Zone Pill Component ──────────────────────────

function ZonePill({ emoji, label, count }: { emoji: string; label: string; count: number }) {
    return (
        <div style={{
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '6px',
            padding: '6px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
        }}>
            <span style={{ fontSize: '12px' }}>{emoji}</span>
            <span style={{ color: '#94a3b8', fontSize: '10px' }}>{label}</span>
            <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '13px', marginLeft: 'auto' }}>
                {count}
            </span>
        </div>
    );
}
